import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Building2, Plus, Pencil, Trash2, ChevronDown, ChevronRight, Users, FileText, Shield } from 'lucide-react';

const RazonesSocialesAdmin = ({ empresaId, language = 'es' }) => {
  const [razones, setRazones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ nombre: '', rfc: '', nombre_representante_legal: '', cargo_representante_legal: '', domicilio_fiscal: '' });

  const t = {
    es: { title: 'Razones Sociales', add: 'Agregar', save: 'Guardar', cancel: 'Cancelar', edit: 'Editar', delete: 'Desactivar', name: 'Nombre / Denominación', rfc: 'RFC', rep: 'Representante Legal', role: 'Cargo', address: 'Domicilio Fiscal', centers: 'Centros de Trabajo', default_badge: 'Principal', confirm_delete: '¿Desactivar esta razón social?', created: 'Razón social creada', updated: 'Razón social actualizada', deleted: 'Razón social desactivada' },
    en: { title: 'Legal Entities', add: 'Add', save: 'Save', cancel: 'Cancel', edit: 'Edit', delete: 'Deactivate', name: 'Legal Name', rfc: 'Tax ID (RFC)', rep: 'Legal Representative', role: 'Role/Title', address: 'Fiscal Address', centers: 'Work Centers', default_badge: 'Primary', confirm_delete: 'Deactivate this legal entity?', created: 'Legal entity created', updated: 'Legal entity updated', deleted: 'Legal entity deactivated' }
  }[language] || {};

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`/organization/razones-sociales/${empresaId}`);
      setRazones(data.data || []);
    } catch { toast.error('Error loading data'); }
    finally { setLoading(false); }
  }, [empresaId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`/organization/razones-sociales/${empresaId}/${editingId}`, form);
        toast.success(t.updated);
      } else {
        await axios.post(`/organization/razones-sociales/${empresaId}`, form);
        toast.success(t.created);
      }
      setShowCreate(false); setEditingId(null);
      setForm({ nombre: '', rfc: '', nombre_representante_legal: '', cargo_representante_legal: '', domicilio_fiscal: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t.confirm_delete)) return;
    try {
      await axios.delete(`/organization/razones-sociales/${empresaId}/${id}`);
      toast.success(t.deleted);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const startEdit = (rs) => {
    setEditingId(rs.id);
    setForm({ nombre: rs.nombre, rfc: rs.rfc || '', nombre_representante_legal: rs.nombre_representante_legal || '', cargo_representante_legal: rs.cargo_representante_legal || '', domicilio_fiscal: rs.domicilio_fiscal || '' });
    setShowCreate(true);
  };

  if (loading) return <div className="text-center py-8 text-gray-400" data-testid="rs-loading">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="razones-sociales-admin">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Building2 className="h-5 w-5" /> {t.title}
        </h3>
        <button
          data-testid="rs-add-btn"
          onClick={() => { setShowCreate(true); setEditingId(null); setForm({ nombre: '', rfc: '', nombre_representante_legal: '', cargo_representante_legal: '', domicilio_fiscal: '' }); }}
          className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 flex items-center gap-1"
        >
          <Plus className="h-4 w-4" /> {t.add}
        </button>
      </div>

      {showCreate && (
        <form onSubmit={handleSubmit} className="bg-gray-50 border rounded-lg p-4 space-y-3" data-testid="rs-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input required placeholder={t.name} value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} className="px-3 py-2 border rounded-md text-sm" data-testid="rs-nombre" />
            <input required placeholder={t.rfc} value={form.rfc} onChange={e => setForm({...form, rfc: e.target.value.toUpperCase()})} className="px-3 py-2 border rounded-md text-sm" maxLength={13} data-testid="rs-rfc" />
            <input required placeholder={t.rep} value={form.nombre_representante_legal} onChange={e => setForm({...form, nombre_representante_legal: e.target.value})} className="px-3 py-2 border rounded-md text-sm" data-testid="rs-rep" />
            <input placeholder={t.role} value={form.cargo_representante_legal} onChange={e => setForm({...form, cargo_representante_legal: e.target.value})} className="px-3 py-2 border rounded-md text-sm" />
          </div>
          <input placeholder={t.address} value={form.domicilio_fiscal} onChange={e => setForm({...form, domicilio_fiscal: e.target.value})} className="w-full px-3 py-2 border rounded-md text-sm" />
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700" data-testid="rs-save-btn">{t.save}</button>
            <button type="button" onClick={() => { setShowCreate(false); setEditingId(null); }} className="px-4 py-2 bg-gray-200 text-sm rounded-lg hover:bg-gray-300">{t.cancel}</button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {razones.map(rs => (
          <div key={rs.id} className="border rounded-lg overflow-hidden" data-testid={`rs-item-${rs.id}`}>
            <div className="flex items-center justify-between p-3 bg-white hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedId(expandedId === rs.id ? null : rs.id)}>
              <div className="flex items-center gap-3">
                {expandedId === rs.id ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                <div>
                  <span className="font-medium text-sm">{rs.nombre}</span>
                  {rs.es_default && <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">{t.default_badge}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> RFC: {rs.rfc || '-'}</span>
                <span className="flex items-center gap-1"><Building2 className="h-3 w-3" /> {rs.centros_activos || 0} {t.centers}</span>
                <button onClick={(e) => { e.stopPropagation(); startEdit(rs); }} className="p-1 hover:bg-gray-200 rounded" data-testid={`rs-edit-${rs.id}`}><Pencil className="h-3.5 w-3.5 text-gray-500" /></button>
                {!rs.es_default && <button onClick={(e) => { e.stopPropagation(); handleDelete(rs.id); }} className="p-1 hover:bg-red-100 rounded" data-testid={`rs-delete-${rs.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>}
              </div>
            </div>
            {expandedId === rs.id && (
              <div className="border-t bg-gray-50 p-3 text-xs text-gray-600 grid grid-cols-2 gap-2">
                <div><span className="font-medium">{t.rep}:</span> {rs.nombre_representante_legal || '-'}</div>
                <div><span className="font-medium">{t.role}:</span> {rs.cargo_representante_legal || '-'}</div>
                <div className="col-span-2"><span className="font-medium">{t.address}:</span> {rs.domicilio_fiscal || '-'}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RazonesSocialesAdmin;
